import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { setCurrentTab, showSideBar, showNavBar } from '../../actions/commonActions'
import { getCertification } from '../../actions/certificationActions'
import { resetCertificationBuilder } from '../../reducers/certificationBuilderReducer'
import { showGenericError } from '../../utility/certificationBuilder'

import Loader from '../common/loader/Loader'
import PageHeader from '../common/PageHeader/PageHeader'
import TablePlaceholder from '../common/TablePlaceholder'
import BuilderStatus from './BuilderStatus'
import BuilderTabs from './BuilderTabs'
import CurriculumTree from './CurriculumTree'
import EditorPanel from './EditorPanel'
import SettingsTab from './SettingsTab'

import classes from './CertificationBuilderPage.module.scss'

const CertificationBuilderPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { certificationId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { certifications, certification, lastSavedAt } = useSelector(state => state.certificationBuilder)
  const [loader, setLoader] = useState(true)
  const [selection, setSelection] = useState(null)

  const tab = searchParams.get('tab') === 'settings' ? 'settings' : 'curriculum'

  useEffect(() => {
    dispatch(setCurrentTab('Certification'))
    dispatch(showSideBar(true))
    dispatch(showNavBar(true))

    return () => dispatch(resetCertificationBuilder())
  }, [dispatch])

  useEffect(() => {
    dispatch(getCertification(certificationId)).unwrap()
      .catch(showGenericError)
      .finally(() => setLoader(false))
  }, [dispatch, certificationId])

  return (
    <div id='certificationBuilderPage'>
      <PageHeader
        title={certification?.name || t('certificationBuilder.page.title')}
        backButton={certifications.length > 1}
        onClickBack={() => navigate('/certifications')}
        actionComponent={certification && <BuilderStatus certification={certification} lastSavedAt={lastSavedAt} />}
      />

      {loader ? (
        <div className={`d-flex align-items-center justify-content-center ${classes.loader}`}>
          <Loader>{t('certificationBuilder.page.loading')}</Loader>
        </div>
      ) : !certification ? (
        <TablePlaceholder message={t('certificationBuilder.page.notFound')} />
      ) : (
        <>
          <BuilderTabs tab={tab} onChange={(nextTab) => setSearchParams(nextTab === 'settings' ? { tab: 'settings' } : {})} />
          <main className={classes.wrapper}>
            {tab === 'curriculum' ? (
              <div className={classes.curriculumLayout}>
                <CurriculumTree certification={certification} selection={selection} onSelect={setSelection} />
                <EditorPanel certification={certification} selection={selection} onSelect={setSelection} />
              </div>
            ) : (
              <SettingsTab certification={certification} />
            )}
          </main>
        </>
      )}
    </div>
  )
}

export default CertificationBuilderPage
